import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, Clock, ExternalLink, CheckCircle, XCircle, List, Calendar as CalendarIcon, Check, Loader2, Clock4, CalendarPlus } from "lucide-react";
import { API_BASE } from "@/lib/site";
import {
  ACTIVITY_TYPES, activityMeta, formatActivityWhen,
  isActivityOverdue, isActivityDueToday, todayISO, tomorrowISO,
} from "./leadHelpers";
import MonthGridView from "./MonthGridView";
import ScheduleActivityModal from "./ScheduleActivityModal";

/* Calendar tab — aggregates pending scheduled_activities across all (active) leads
 * and groups them into Today / Upcoming / Overdue. Read-only: editing happens on
 * the lead detail page where the Reminders tab already exists. */

function buildItems(leads) {
  const out = [];
  for (const l of leads || []) {
    for (const a of (l.scheduled_activities || [])) {
      if (a.status !== "pending") continue; // only pending follow-ups feed the calendar list
      out.push({ activity: a, lead: l });
    }
  }
  out.sort((a, b) => {
    const ad = `${a.activity.date || ""}T${(a.activity.time || "00:00")}`;
    const bd = `${b.activity.date || ""}T${(b.activity.time || "00:00")}`;
    return ad.localeCompare(bd);
  });
  return out;
}

function ActivityCard({ activity, lead, onComplete, onSnooze }) {
  const meta = activityMeta(activity.type);
  const overdue = isActivityOverdue(activity);
  const due = isActivityDueToday(activity);
  const [busyAction, setBusyAction] = useState(null); // "complete" | "snooze" | null

  const handleComplete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onComplete || busyAction) return;
    setBusyAction("complete");
    try { await onComplete(lead.id, activity.id); } finally { setBusyAction(null); }
  };
  const handleSnooze = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onSnooze || busyAction) return;
    setBusyAction("snooze");
    try { await onSnooze(lead.id, activity.id); } finally { setBusyAction(null); }
  };

  return (
    <Link
      to={`/admin/leads/${lead.id}`}
      data-testid={`calendar-item-${activity.id || `${lead.id}-${activity.date}-${activity.time || "x"}`}`}
      className={`group block rounded-xl border bg-white p-4 hover:border-[var(--swipe-accent)] hover:shadow-[0_8px_20px_-12px_rgba(0,114,255,0.35)] transition-all ${
        overdue
          ? "border-rose-200 bg-rose-50/40"
          : due
            ? "border-[var(--swipe-accent)]/30 bg-[var(--swipe-accent-soft)]/30"
            : "border-slate-200"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          overdue ? "bg-rose-100 text-rose-700" : due ? "bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)]" : "bg-slate-100 text-slate-600"
        }`}>
          {meta.group === "appointment" ? <CalendarClock className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--swipe-accent)]">
              {meta.label}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              · {meta.group === "appointment" ? "Appointment" : "Follow-up"}
            </span>
            {overdue ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-rose-700 bg-rose-100">
                <AlertTriangle className="w-3 h-3" /> Overdue
              </span>
            ) : due ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-[var(--swipe-accent)] bg-[var(--swipe-accent-soft)]">
                Today
              </span>
            ) : null}
          </div>
          <div className="mt-1 font-display text-[15px] font-bold text-[var(--swipe-navy)] truncate">
            {lead.business_name || lead.contact_name || "Unnamed lead"}
          </div>
          <div className="mt-0.5 text-[13px] text-slate-600 truncate">
            {lead.contact_name && lead.business_name ? lead.contact_name : ""}
            {lead.contact_name && lead.business_name ? " · " : ""}
            {formatActivityWhen(activity)}
          </div>
          {activity.note ? (
            <p className="mt-2 text-[13px] text-slate-600 leading-relaxed line-clamp-2">{activity.note}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[var(--swipe-accent)] transition-colors mt-1" />
          {onComplete && activity.id ? (
            <button
              type="button"
              onClick={handleComplete}
              disabled={!!busyAction}
              data-testid={`calendar-item-complete-${activity.id}`}
              title="Mark complete"
              aria-label="Mark activity complete"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold border border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {busyAction === "complete" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              <span>Done</span>
            </button>
          ) : null}
          {overdue && onSnooze && activity.id ? (
            <button
              type="button"
              onClick={handleSnooze}
              disabled={!!busyAction}
              data-testid={`calendar-item-snooze-${activity.id}`}
              title="Snooze to tomorrow"
              aria-label="Snooze activity to tomorrow"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold border border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {busyAction === "snooze" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock4 className="w-3 h-3" />}
              <span>Snooze</span>
            </button>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function Section({ title, items, accent = "navy", emptyLabel, icon: Icon, onComplete, onSnooze }) {
  const accentClass =
    accent === "rose"
      ? "bg-rose-100 text-rose-700"
      : accent === "blue"
        ? "bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)]"
        : "bg-slate-100 text-slate-600";
  return (
    <section data-testid={`calendar-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accentClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="font-display text-[16px] font-extrabold text-[var(--swipe-navy)] tracking-tight">{title}</h3>
        <span className="inline-flex items-center justify-center min-w-[22px] h-[20px] rounded-full px-1.5 text-[10.5px] font-bold bg-slate-100 text-slate-600">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-8 text-center text-[13px] text-slate-400">
          {emptyLabel}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((it) => (
            <ActivityCard
              key={`${it.lead.id}-${it.activity.id || `${it.activity.date}-${it.activity.time || "x"}`}`}
              {...it}
              onComplete={onComplete}
              onSnooze={onSnooze}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function CalendarTab({ leads, onLeadPatched }) {
  const items = useMemo(() => buildItems(leads || []), [leads]);
  const today = todayISO();
  const [viewMode, setViewMode] = useState("list"); // "list" | "month"
  const [scheduleOpen, setScheduleOpen] = useState(false);

  /* Called by the Schedule Activity modal after a successful POST. Merges
   * the newly created activity into the parent leads state so it appears
   * immediately in List + Month views (and on the related lead detail). */
  const handleActivityCreated = (leadId, activity) => {
    if (!onLeadPatched || !activity) return;
    const lead = (leads || []).find((l) => l.id === leadId);
    const next = [...((lead?.scheduled_activities) || []), activity];
    onLeadPatched(leadId, { scheduled_activities: next });
  };

  /* PATCH activity → completed, then push the updated activities array back up
   * to AdminDashboard via onLeadPatched so List/Month views both update. */
  const completeActivity = async (leadId, activityId) => {
    try {
      const r = await axios.patch(
        `${API_BASE}/admin/leads/${leadId}/scheduled-activity/${activityId}`,
        { status: "completed" },
        { withCredentials: true },
      );
      const updated = r.data?.activity;
      if (!updated || !onLeadPatched) {
        toast.success("Marked complete");
        return;
      }
      // Merge: replace the matching activity in the lead's scheduled_activities.
      const lead = (leads || []).find((l) => l.id === leadId);
      if (lead) {
        const next = (lead.scheduled_activities || []).map((a) =>
          a.id === activityId ? { ...a, ...updated } : a,
        );
        onLeadPatched(leadId, { scheduled_activities: next });
      }
      toast.success("Marked complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to mark complete");
    }
  };

  /* PATCH activity → date = tomorrow (keeps the time unchanged).
   * Used by the "Snooze 1 day" inline action on overdue items. */
  const snoozeActivity = async (leadId, activityId) => {
    try {
      const newDate = tomorrowISO();
      const r = await axios.patch(
        `${API_BASE}/admin/leads/${leadId}/scheduled-activity/${activityId}`,
        { date: newDate },
        { withCredentials: true },
      );
      const updated = r.data?.activity;
      if (!updated || !onLeadPatched) {
        toast.success("Snoozed to tomorrow");
        return;
      }
      const lead = (leads || []).find((l) => l.id === leadId);
      if (lead) {
        const next = (lead.scheduled_activities || []).map((a) =>
          a.id === activityId ? { ...a, ...updated } : a,
        );
        onLeadPatched(leadId, { scheduled_activities: next });
      }
      toast.success("Snoozed to tomorrow");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to snooze");
    }
  };

  const overdueItems = items.filter((it) => isActivityOverdue(it.activity));
  const todayItems = items.filter(
    (it) => isActivityDueToday(it.activity) && !isActivityOverdue(it.activity)
  );
  const upcomingItems = items.filter(
    (it) => (it.activity.date || "") > today
  );

  return (
    <div data-testid="calendar-tab" className="space-y-7">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
            <CalendarClock className="w-6 h-6" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h2 className="font-display-tight text-[1.4rem] sm:text-[1.6rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                Calendar
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Schedule Activity — opens modal with lead picker */}
                <button
                  type="button"
                  onClick={() => setScheduleOpen(true)}
                  data-testid="calendar-schedule-activity"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[var(--swipe-accent)]/90 text-white text-[12.5px] font-bold shadow-sm hover:shadow transition-all"
                >
                  <CalendarPlus className="w-3.5 h-3.5" /> Schedule Activity
                </button>
                {/* View toggle — List | Month */}
                <div
                  role="tablist"
                  data-testid="calendar-view-toggle"
                  className="inline-flex items-center rounded-lg border border-slate-200 overflow-hidden"
                >
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                  data-testid="calendar-view-list"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold border-r border-slate-200 transition-colors ${
                    viewMode === "list"
                      ? "bg-[var(--swipe-accent)] text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <List className="w-3.5 h-3.5" /> List
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === "month"}
                  onClick={() => setViewMode("month")}
                  data-testid="calendar-view-month"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                    viewMode === "month"
                      ? "bg-[var(--swipe-accent)] text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <CalendarIcon className="w-3.5 h-3.5" /> Month
                </button>
                </div>
              </div>
            </div>
            <p className="mt-2 text-[14px] text-slate-600 leading-[1.65] max-w-2xl">
              All scheduled lead activity in one place — calls, texts, emails, meetings, and follow-ups.
              Click any item to open the related lead.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {ACTIVITY_TYPES.map((a) => (
                <span
                  key={a.v}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-slate-100 text-slate-700"
                >
                  {a.label}
                </span>
              ))}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-emerald-50 text-emerald-700">
                <CheckCircle className="w-3 h-3" /> Completed
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-slate-100 text-slate-500">
                <XCircle className="w-3 h-3" /> Canceled
              </span>
            </div>
          </div>
        </div>
      </div>

      {viewMode === "list" ? (
        <>
          <Section
            title="Overdue"
            items={overdueItems}
            accent="rose"
            icon={AlertTriangle}
            emptyLabel="Nothing overdue. You're caught up."
            onComplete={completeActivity}
            onSnooze={snoozeActivity}
          />
          <Section
            title="Today"
            items={todayItems}
            accent="blue"
            icon={CalendarClock}
            emptyLabel="Nothing scheduled for today yet."
            onComplete={completeActivity}
          />
          <Section
            title="Upcoming"
            items={upcomingItems}
            accent="navy"
            icon={Clock}
            emptyLabel="No upcoming scheduled activities. Add reminders from any lead's detail page."
            onComplete={completeActivity}
          />
        </>
      ) : (
        <MonthGridView leads={leads} onComplete={completeActivity} onSnooze={snoozeActivity} />
      )}

      <ScheduleActivityModal
        open={scheduleOpen}
        leads={leads}
        onClose={() => setScheduleOpen(false)}
        onCreated={handleActivityCreated}
      />
    </div>
  );
}
